/**
 * Shared sub-step progress logic for analysis jobs and task execution.
 *
 * The backend emits `analysis-progress` with {step, description} (no workflow)
 * for live log lines during long-running steps (apk installs, git clone, etc.).
 * Both the analysis job page and the task execution page use the same event;
 * socket.io room delivery ensures each client only receives its own events.
 */

import { useEffect } from 'react';
import type { Socket } from 'socket.io-client';

interface SubStepProgressEvent {
  step?: string;
  description?: string;
}

// Minimal structural constraints — both JobStatus and TaskStatus satisfy these
interface WithSubSteps {
  workflow?: { steps: Array<{ title: string; status?: string; description: string }> } | null;
  status?: string;
}

/**
 * Pure updater: finds the currently running step and updates its title/description
 * in-place from the backend live-progress event. Returns prev unchanged if no
 * running step is found. The backend updates both fields freely so the UI shows
 * dynamic progress (e.g. apk add output, git clone percentages).
 */
export function applySubStepProgress<T extends WithSubSteps>(
  prev: T | null,
  data: SubStepProgressEvent,
): T | null {
  if (!prev?.workflow?.steps || (!data.step && !data.description)) return prev;
  if (prev.status === 'failed' || prev.status === 'completed' || prev.status === 'cancelled') {
    return prev;
  }
  const updatedSteps = prev.workflow.steps.map((s) =>
    s.status === 'running'
      ? { ...s, title: data.step ?? s.title, description: data.description ?? s.description }
      : s,
  );
  return { ...prev, workflow: { ...prev.workflow, steps: updatedSteps } } as T;
}

/**
 * Hook: subscribes to `analysis-progress` sub-step events on the given socket
 * and applies description updates to the provided state setter.
 *
 * Works for both useAnalysisJob and useTaskExecution — the socket room ensures
 * each page only receives events belonging to its own job/task.
 */
export function useSubStepProgress<T extends WithSubSteps>(
  socket: Socket | null | undefined,
  setStatus: React.Dispatch<React.SetStateAction<T | null>>,
): void {
  useEffect(() => {
    if (!socket) return;
    const handler = (data: SubStepProgressEvent) => {
      setStatus((prev) => applySubStepProgress(prev, data));
    };
    socket.on('analysis-progress', handler);
    return () => {
      socket.off('analysis-progress', handler);
    };
  }, [socket, setStatus]);
}
